import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Plane, MapPin, Wind, Camera, CheckCircle2, ChevronRight, Navigation } from 'lucide-react';

const DronePilot: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "SkyHigh Aviation Academy";
    
    return (
        <div className="min-h-screen bg-sky-50 font-sans text-slate-800 selection:bg-sky-200 selection:text-sky-900">
            {/* Top Bar for FAA Status */}
            <div className="bg-sky-900 text-white py-2 px-6 text-xs font-bold text-center tracking-wide">
                FAA PART 107 CERTIFIED TRAINING CENTER • ENROLL FOR FALL 2026
            </div>

            {/* Navbar */}
            <nav className="sticky top-0 z-50 bg-white border-b border-sky-100 shadow-sm">
                <div className="max-w-7xl mx-auto px-6 h-20 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-sky-600 rounded-full flex items-center justify-center text-white shadow-md">
                            <Plane size={20} className="transform -rotate-45" />
                        </div>
                        <span className="font-bold text-xl tracking-tight text-slate-900">{schoolName}</span>
                    </div>

                    <div className="hidden md:flex items-center gap-8 font-medium text-sm text-slate-600">
                        <a href="#" className="hover:text-sky-600 transition-colors">Flight Training</a>
                        <a href="#" className="hover:text-sky-600 transition-colors">Certification</a>
                        <a href="#" className="hover:text-sky-600 transition-colors">Fleet</a>
                        <a href="#" className="hover:text-sky-600 transition-colors">Locations</a>
                    </div>

                    <div className="flex gap-4">
                        <Button variant="ghost" className="hidden md:flex text-sky-700 hover:bg-sky-50 font-bold">
                            Student Login
                        </Button>
                        <Button className="bg-sky-600 hover:bg-sky-700 text-white font-bold rounded-full px-6 shadow-lg shadow-sky-200">
                            Start Flying
                        </Button>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <header className="relative py-28 px-6 overflow-hidden">
                <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center relative z-10">
                    <div>
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-sky-100 text-sky-600 text-xs font-bold uppercase tracking-wider mb-8 shadow-sm">
                            <CheckCircle2 size={14} /> Official Licensing Partner
                        </div>
                        <h1 className="text-5xl md:text-6xl font-black text-slate-900 leading-tight mb-6">
                            Master the Skies. <br/>
                            <span className="text-sky-600">Professionally.</span>
                        </h1>
                        <p className="text-xl text-slate-500 mb-10 max-w-lg leading-relaxed">
                            From commercial surveying to cinematography, get the skills and certification you need to launch a career in unmanned aviation.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4">
                            <Button className="h-14 px-8 bg-sky-600 hover:bg-sky-700 text-white font-bold rounded-lg text-base shadow-lg shadow-sky-900/20">
                                View Courses
                            </Button>
                            <Button variant="outline" className="h-14 px-8 border-sky-200 text-sky-700 hover:bg-sky-50 font-bold rounded-lg text-base bg-white">
                                Meet Instructors
                            </Button>
                        </div>
                    </div>

                    {/* Hero Image Card */}
                    <div className="relative aspect-[4/3] bg-white rounded-3xl p-4 shadow-2xl rotate-2 hover:rotate-0 transition-transform duration-500 border border-slate-100">
                        <div className="absolute inset-0 bg-gradient-to-br from-sky-400/20 to-indigo-400/20 rounded-3xl transform translate-x-4 translate-y-4 -z-10"></div>
                        <div className="h-full w-full bg-slate-200 rounded-2xl overflow-hidden relative">
                             <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1506947411487-a5673826e631?q=80&w=2000&auto=format&fit=crop')] bg-cover bg-center"></div>
                             <div className="absolute bottom-6 left-6 right-6 bg-white/90 backdrop-blur-md p-4 rounded-xl shadow-lg border border-white/50">
                                 <div className="flex justify-between items-center">
                                     <div>
                                         <div className="text-xs text-sky-600 font-bold uppercase tracking-wider mb-1">Advanced Maneuvers</div>
                                         <div className="font-bold text-slate-900">Cinematography 101</div>
                                     </div>
                                     <div className="w-10 h-10 bg-sky-100 rounded-full flex items-center justify-center text-sky-600">
                                         <Camera size={20} />
                                     </div>
                                 </div>
                             </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* Courses / Features */}
            <section className="py-24 px-6 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold text-slate-900 mb-4">Professional Tracks</h2>
                        <div className="w-16 h-1.5 bg-sky-500 mx-auto rounded-full"></div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[
                            { icon: MapPin, title: "Surveying & Mapping", desc: "Photogrammetry and LiDAR scanning for construction and agriculture." },
                            { icon: Camera, title: "Aerial Cinematography", desc: "Complex camera movements and composition for film and TV." },
                            { icon: Navigation, title: "Inspection Services", desc: "Thermal imaging and structural inspection for infrastructure." }
                        ].map((item, i) => (
                            <div key={i} className="group p-8 rounded-2xl bg-slate-50 border border-slate-100 hover:bg-white hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                                <div className="w-14 h-14 bg-white rounded-xl shadow-sm text-sky-600 flex items-center justify-center mb-6 border border-slate-100 group-hover:scale-110 transition-transform">
                                    <item.icon size={28} />
                                </div>
                                <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                                <p className="text-slate-500 leading-relaxed mb-8">{item.desc}</p>
                                <a href="#" className="flex items-center gap-2 text-sky-600 font-bold text-sm hover:gap-3 transition-all">
                                    Explore Track <ChevronRight size={16} />
                                </a>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

             {/* Footer */}
            <footer className="bg-slate-900 text-white py-20 px-6">
                <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-12">
                    <div className="col-span-1 md:col-span-2">
                        <div className="flex items-center gap-3 mb-6">
                            <Plane size={24} className="text-sky-400 transform -rotate-45" />
                            <span className="font-bold text-xl tracking-tight">{schoolName}</span>
                        </div>
                        <p className="text-slate-400 max-w-sm leading-relaxed text-sm">
                            Training the next generation of pilots for the future of aviation. Fully accredited and FAA compliant.
                        </p>
                    </div>
                    
                    <div>
                        <h4 className="font-bold mb-6 text-sm uppercase tracking-wider text-sky-400">Quick Links</h4>
                        <ul className="space-y-3 text-sm text-slate-300">
                            <li><a href="#" className="hover:text-white transition-colors">Course Catalog</a></li>
                            <li><a href="#" className="hover:text-white transition-colors">Admissions</a></li>
                            <li><a href="#" className="hover:text-white transition-colors">FAQ</a></li>
                        </ul>
                    </div>

                    <div>
                        <h4 className="font-bold mb-6 text-sm uppercase tracking-wider text-sky-400">Contact</h4>
                        <ul className="space-y-3 text-sm text-slate-300">
                            <li>flights@skyhigh.edu</li>
                            <li>+1 (800) FLY-X107</li>
                            <li>Hangar 4, Airfield Dr.</li>
                        </ul>
                    </div>
                </div>
                <div className="max-w-7xl mx-auto mt-16 pt-8 border-t border-slate-800 text-center text-xs text-slate-500">
                    &copy; 2026 {schoolName}.
                </div>
            </footer>
        </div>
    );
};

export default DronePilot;
